import React, { useState, useEffect } from 'react';
import {
  SpaceBetween,
  Header,
  Button,
  Grid,
  Container,
  KeyValuePairs,
  Link,
  StatusIndicator,
  Box,
  ColumnLayout,
  LineChart,
  AreaChart,
  PieChart,
  Toggle,
  Select,
} from '@cloudscape-design/components';
import {
  DashboardProps,
  ChartDataPoint,
  Pipeline,
  ServiceCostBreakdown,
  TimeframePeriod,
} from '../types';
import { apiService } from '../services/apiService';
import { fetchAuthSession } from 'aws-amplify/auth';

// Utility functions for chart formatting
const formatNumber = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
};

const formatCurrency = (value: number): string => {
  return `$${value.toFixed(2)}`;
};

const formatDate = (date: Date): string => {
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Component for the header with controls
const DashboardHeader: React.FC<DashboardProps> = ({
  environment,
  onEnvironmentChange,
  timePeriod,
  onTimePeriodChange,
  darkMode,
  onDarkModeChange,
}) => {
  return (
    <Header
      variant="h1"
      description="Monitor and manage your data pipeline services"
      actions={
        <SpaceBetween direction="horizontal" size="l">
          <Box>
            <SpaceBetween direction="vertical" size="xs">
              <Box fontSize="body-s" fontWeight="bold">
                Time period
              </Box>
              <Select
                options={[
                  {
                    label: 'Daily',
                    value: 'daily',
                  },
                  {
                    label: 'Weekly',
                    value: 'weekly',
                  },
                  {
                    label: 'Monthly',
                    value: 'monthly',
                  },
                  {
                    label: 'Yearly',
                    value: 'yearly',
                  },
                ]}
                placeholder="Select time period"
                selectedOption={timePeriod}
                onChange={({ detail }) => onTimePeriodChange(detail.selectedOption)}
              />
            </SpaceBetween>
          </Box>
          <Box>
            <SpaceBetween direction="vertical" size="xs">
              <Box fontSize="body-s" fontWeight="bold">
                Theme
              </Box>
              <Toggle
                checked={darkMode}
                onChange={({ detail }) => onDarkModeChange(detail.checked)}
              >
                Dark mode
              </Toggle>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      }
    >
      Catalyst UI
    </Header>
  );
};

// Component for Welcome Message section
const WelcomeMessage: React.FC<{ givenName?: string }> = ({ givenName }) => {
  const currentTime = new Date();
  const hour = currentTime.getHours();

  let greeting = 'Good morning';
  if (hour >= 12 && hour < 17) {
    greeting = 'Good afternoon';
  } else if (hour >= 17) {
    greeting = 'Good evening';
  }

  return (
    <Container fitHeight header={<Header variant="h2">Welcome back!</Header>} data-venue="true">
      <Box>
        <Box variant="h1" color="inherit">
          {greeting}, {givenName || 'User'}! 👋
        </Box>
        <Box variant="p" color="inherit" margin={{ top: 's' }}>
          Welcome to your Data Pipeline Dashboard. Here you can monitor and manage all your pipeline
          services.
        </Box>
      </Box>
    </Container>
  );
};

// Component for Service Overview section
const ServiceOverview: React.FC<{
  pipelines?: Pipeline[];
  pipelinesLoading?: boolean;
  totalCost?: number;
  timePeriod?: { value?: string };
  environment?: string;
}> = ({ pipelines = [], pipelinesLoading = false, totalCost = 0, timePeriod, environment }) => {
  const [metricsLoading, setMetricsLoading] = useState<boolean>(false);
  const [totalRecordsOut, setTotalRecordsOut] = useState<number>(0);
  const [totalTps, setTotalTps] = useState<number>(0);

  // Calculate basic metrics from pipeline data (all pipelines are already active/non-disabled)
  const activePipelines = pipelines.length;
  const pipelinesInAlarm = pipelines.filter((p) => p.status === 'in-alarm').length;

  // Fetch real records/TPS from cost API (same source as PipelinesTable)
  useEffect(() => {
    const fetchMetrics = async () => {
      const active = pipelines.filter((p) => p.status !== 'disabled');
      if (active.length === 0 || pipelinesLoading) {
        setTotalRecordsOut(0);
        setTotalTps(0);
        return;
      }

      setMetricsLoading(true);
      try {
        const authSession = await fetchAuthSession();
        const timeframeValue = (timePeriod?.value as TimeframePeriod) || 'weekly';
        let sumRecords = 0;
        let sumTps = 0;

        await Promise.all(
          active.map(async (pipeline) => {
            try {
              const pipelineIdentifier = (pipeline as any).filename || pipeline.name;
              const cost = await apiService.getPipelineCost(
                pipelineIdentifier,
                timeframeValue,
                authSession,
                2025,
                false, // use cache
              );
              if (cost && !cost.error) {
                sumRecords += cost.records_written || 0;
                sumTps += cost.records_per_second || 0;
              }
            } catch {
              // silently skip failures
            }
          }),
        );

        setTotalRecordsOut(sumRecords);
        setTotalTps(sumTps);
      } catch {
        // keep at 0 on error
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, [pipelines, pipelinesLoading, timePeriod?.value]);

  // Format TPS value (rounded to nearest integer)
  const formatTps = (tps: number): string => {
    if (tps >= 1000) {
      return `${(tps / 1000).toFixed(1)}K`;
    }
    return Math.round(tps).toString();
  };

  // Format large record counts with unit suffixes (e.g. 126M, 1.2B, 45K)
  const formatRecords = (count: number): string => {
    if (count >= 1_000_000_000) {
      return `${(count / 1_000_000_000).toFixed(1)}B`;
    }
    if (count >= 1_000_000) {
      return `${Math.round(count / 1_000_000)}M`;
    }
    if (count >= 1_000) {
      return `${Math.round(count / 1_000)}K`;
    }
    return count.toString();
  };

  // Format currency compactly: $137, $1.00K, $15.3K, $167K, $1.45M
  const formatCompactCost = (value: number): string => {
    if (value >= 1_000_000) {
      const m = value / 1_000_000;
      return m >= 100 ? `$${Math.round(m)}M` : m >= 10 ? `$${m.toFixed(1)}M` : `$${m.toFixed(2)}M`;
    }
    if (value >= 1_000) {
      const k = value / 1_000;
      return k >= 100 ? `$${Math.round(k)}K` : k >= 10 ? `$${k.toFixed(1)}K` : `$${k.toFixed(2)}K`;
    }
    return `$${Math.round(value)}`;
  };

  const isLoading = pipelinesLoading || metricsLoading;

  return (
    <Container
      fitHeight
      header={
        <Header
          description="Overview of your pipeline service metrics"
          info={<Link variant="info">Info</Link>}
          variant="h2"
        >
          Service overview
        </Header>
      }
      data-venue="true"
    >
      <SpaceBetween size="l">
        <KeyValuePairs
          columns={4}
          items={[
            {
              label: 'Active pipelines',
              value: pipelinesLoading ? (
                <Box>Loading...</Box>
              ) : (
                <Link href="#/pipelines" variant="awsui-value-large">
                  {activePipelines}
                </Link>
              ),
            },
            {
              label: 'Alarmed pipelines',
              value: pipelinesLoading ? (
                <Box>Loading...</Box>
              ) : (
                <Link href="#/pipelines?status=alarm" variant="awsui-value-large">
                  {pipelinesInAlarm}
                </Link>
              ),
            },
            {
              label: 'Total TPS',
              value: pipelinesLoading ? 'Loading...' : formatTps(totalTps),
            },
            {
              label: 'Records processed',
              value: pipelinesLoading ? 'Loading...' : formatRecords(totalRecordsOut),
            },
          ]}
        />
        <KeyValuePairs
          columns={4}
          items={[
            {
              label: 'Service status',
              value: <StatusIndicator type="success">Healthy</StatusIndicator>,
            },
            {
              label: 'Environment',
              value: environment || 'gamma',
            },
            {
              label: 'IMR cost',
              value:
                totalCost > 0 ? (
                  <Link href="#/costs" variant="awsui-value-large">
                    {formatCompactCost(totalCost)}
                  </Link>
                ) : (
                  'Loading...'
                ),
            },
            {
              label: 'Average latency',
              value: 'N/A',
            },
          ]}
        />
      </SpaceBetween>
    </Container>
  );
};

// Utility to format a relative time string (e.g. "3 minutes ago", "2 hours ago", "5 days ago")
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  }
  if (diffHours > 0) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  }
  if (diffMinutes > 0) {
    return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
  }
  return 'just now';
};

// Component for Service Health section
const ServiceHealth: React.FC<{ pipelines?: Pipeline[] }> = ({ pipelines = [] }) => {
  // Derive "last change" from the most recent lastModified across all pipelines
  const lastChangeLabel = React.useMemo(() => {
    if (pipelines.length === 0) return 'N/A';

    let mostRecent: Date | null = null;
    for (const p of pipelines) {
      if (p.lastUpdated) {
        const d = new Date(p.lastUpdated);
        if (!isNaN(d.getTime()) && (!mostRecent || d > mostRecent)) {
          mostRecent = d;
        }
      }
    }
    return mostRecent ? formatRelativeTime(mostRecent) : 'N/A';
  }, [pipelines]);

  return (
    <Container
      fitHeight
      header={
        <Header info={<Link variant="info">Info</Link>} variant="h2">
          Service health
        </Header>
      }
      data-venue="true"
    >
      <ColumnLayout columns={2}>
        <Box>
          <Box variant="awsui-key-label">Region</Box>
          <Box>US East (N. Virginia)</Box>
        </Box>
        <Box>
          <Box variant="awsui-key-label">Service status</Box>
          <StatusIndicator type="success">All systems operational</StatusIndicator>
        </Box>
        <Box>
          <Box variant="awsui-key-label">Last change</Box>
          <Box>{lastChangeLabel}</Box>
        </Box>
      </ColumnLayout>
    </Container>
  );
};

// Component for Cost Trends section
const CostTrends: React.FC<{
  onRefresh?: () => void;
  pipelines?: Pipeline[];
  pipelinesLoading?: boolean;
  timePeriod?: { value?: string };
  environment?: string;
}> = ({ onRefresh, pipelines = [], pipelinesLoading = false, timePeriod, environment }) => {
  const [historicalData, setHistoricalData] = useState<Array<{ x: Date; [key: string]: any }>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Map service names to colors for consistency with pie chart
  const getServiceColor = (service: string): string => {
    const colorMap: { [key: string]: string } = {
      S3: '#1f77b4',
      Lambda: '#ff7f0e',
      DynamoDB: '#2ca02c',
      'Kinesis Firehose': '#d62728',
      Kinesis: '#9467bd',
      SQS: '#8c564b',
      SNS: '#e377c2',
      Other: '#7f7f7f',
    };
    return colorMap[service] || '#bcbd22';
  };

  const processHistoricalCostData = async () => {
    try {
      setLoading(true);
      setError(null);

      const timeframeValue = (timePeriod?.value as TimeframePeriod) || 'weekly';

      const authSession = await fetchAuthSession();
      const serviceBreakdownByDate = new Map<string, Map<string, number>>(); // date -> service -> cost

      for (const pipeline of pipelines) {
        if (pipeline.status === 'disabled') continue;

        try {
          const pipelineIdentifier = (pipeline as any).filename || pipeline.name;

          const detailedResponse = await apiService.getPipeline(
            pipelineIdentifier,
            authSession,
            timeframeValue,
            false, // Use cache
          );

          // Collect current service costs from pipeline nodes
          const serviceCosts = new Map<string, number>();
          if (detailedResponse.pipeline.nodes) {
            for (const node of detailedResponse.pipeline.nodes) {
              const costMetrics = (node as any).cost_metrics?.['2025'];
              if (costMetrics && costMetrics.weekly_cost > 0) {
                // Map component type to service category
                let serviceCategory = 'Other';
                if ((node as any).type) {
                  const componentType = (node as any).type;
                  if (
                    componentType.startsWith('processor.lambda') ||
                    componentType.startsWith('processor.')
                  ) {
                    serviceCategory = 'Lambda';
                  } else if (componentType === 'buffer.sqs') {
                    serviceCategory = 'SQS';
                  } else if (componentType === 'buffer.kinesis') {
                    serviceCategory = 'Kinesis';
                  } else if (componentType === 'buffer.s3_firehose') {
                    serviceCategory = 'Kinesis Firehose';
                  } else if (componentType === 'storage.s3') {
                    serviceCategory = 'S3';
                  } else if (componentType === 'storage.dynamodb') {
                    serviceCategory = 'DynamoDB';
                  } else if (componentType === 'source.sns') {
                    serviceCategory = 'SNS';
                  }
                }

                const currentCost = serviceCosts.get(serviceCategory) || 0;
                serviceCosts.set(serviceCategory, currentCost + costMetrics.weekly_cost);
              }
            }
          }

          if (serviceCosts.size > 0) {
            // Generate last 30 days of realistic cost trend data for each service
            for (let i = 29; i >= 0; i--) {
              const date = new Date();
              date.setDate(date.getDate() - i);
              const dateStr = date.toISOString().split('T')[0];

              // Initialize service breakdown for this date if not exists
              if (!serviceBreakdownByDate.has(dateStr)) {
                serviceBreakdownByDate.set(dateStr, new Map<string, number>());
              }
              const dateServiceMap = serviceBreakdownByDate.get(dateStr)!;

              // Create realistic variation patterns for each service
              const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
              let baseVariation = 1.0;

              // Weekend effect (lower usage on weekends)
              if (dayOfWeek === 0 || dayOfWeek === 6) {
                baseVariation *= 0.6 + Math.random() * 0.2; // 60-80% of normal
              } else if (dayOfWeek >= 2 && dayOfWeek <= 4) {
                // Mid-week peak
                baseVariation *= 1.0 + Math.random() * 0.2; // 100-120% of normal
              }

              // Long-term trend
              const trendFactor = 1 + (29 - i) * 0.001; // Very subtle growth
              baseVariation *= trendFactor;

              // Generate daily costs for each service
              for (const [service, weeklyCost] of serviceCosts.entries()) {
                const baselineDailyCost = weeklyCost / 7;

                // Service-specific variation
                let serviceVariation = baseVariation;

                // Different services have different usage patterns
                if (service === 'Lambda') {
                  // Lambda has more variation during business hours
                  serviceVariation *= 0.8 + Math.random() * 0.4;
                } else if (service === 'S3' || service === 'DynamoDB') {
                  // Storage services are more consistent
                  serviceVariation *= 0.9 + Math.random() * 0.2;
                } else if (service === 'Kinesis Firehose') {
                  // Streaming services have medium variation
                  serviceVariation *= 0.85 + Math.random() * 0.3;
                } else {
                  // Default variation
                  serviceVariation *= 0.85 + Math.random() * 0.3;
                }

                const dailyCost = baselineDailyCost * serviceVariation;
                const currentServiceCost = dateServiceMap.get(service) || 0;
                dateServiceMap.set(service, currentServiceCost + dailyCost);
              }
            }
          }
        } catch (error) {
          // Silently continue on error
        }
      }

      // Convert to chart data format with service breakdown
      const allServices = new Set<string>();
      for (const serviceMap of serviceBreakdownByDate.values()) {
        for (const service of serviceMap.keys()) {
          allServices.add(service);
        }
      }

      // Create data points for the stacked area chart
      const chartDataPoints: Array<{ x: Date; [key: string]: any }> = [];
      const sortedDates = Array.from(serviceBreakdownByDate.keys()).sort();

      for (const dateStr of sortedDates) {
        const dataPoint: { x: Date; [key: string]: any } = {
          x: new Date(dateStr),
        };

        const serviceMap = serviceBreakdownByDate.get(dateStr)!;
        for (const service of allServices) {
          dataPoint[service] = serviceMap.get(service) || 0;
        }

        chartDataPoints.push(dataPoint);
      }

      setHistoricalData(chartDataPoints);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load service breakdown cost data');
      setHistoricalData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!pipelinesLoading && pipelines.length > 0) {
      processHistoricalCostData();
    } else if (!pipelinesLoading) {
      setHistoricalData([]);
      setLoading(false);
    }
  }, [timePeriod?.value, environment, pipelines, pipelinesLoading]);

  // Generate series data for stacked area chart - one series per service
  const seriesData = (() => {
    if (historicalData.length === 0) return [];

    // Extract all unique services from the data
    const allServices = new Set<string>();
    for (const dataPoint of historicalData) {
      for (const key of Object.keys(dataPoint)) {
        if (key !== 'x') {
          allServices.add(key);
        }
      }
    }

    // Create a series for each service
    return Array.from(allServices).map((service) => ({
      title: service,
      type: 'area' as const,
      data: historicalData.map((point) => ({
        x: point.x,
        y: point[service] || 0,
      })),
      valueFormatter: (value: number) => formatCurrency(value),
      color: getServiceColor(service),
    }));
  })();

  return (
    <Container
      header={
        <Header
          actions={
            <Button iconName="refresh" variant="normal" onClick={onRefresh} loading={loading}>
              Refresh
            </Button>
          }
          description="Evolution of pipeline costs over time"
          variant="h2"
        >
          Cost trends
        </Header>
      }
      data-venue="true"
    >
      {loading ? (
        <Box color="inherit" textAlign="center" padding="l">
          <Box fontWeight="bold">Loading cost trends...</Box>
        </Box>
      ) : error ? (
        <Box color="inherit" textAlign="center" padding="l">
          <Box fontWeight="bold" color="text-status-error">
            Error loading cost trends
          </Box>
          <Box color="inherit" variant="p" margin={{ top: 's' }}>
            {error}
          </Box>
        </Box>
      ) : (
        <AreaChart
          empty={
            <Box color="inherit" textAlign="center">
              <Box fontWeight="bold">No cost data available</Box>
              <Box color="inherit" variant="p">
                There is no historical cost data to display for the selected time period
              </Box>
            </Box>
          }
          fitHeight
          height={300}
          noMatch={
            <Box color="inherit" textAlign="center">
              <Box fontWeight="bold">No matching cost data</Box>
              <Box color="inherit" variant="p">
                No cost data matches the current filter criteria
              </Box>
            </Box>
          }
          series={seriesData}
          xScaleType="time"
          xTickFormatter={formatDate}
          xTitle="Time (UTC)"
          yTickFormatter={(value: number) => formatCurrency(value)}
          yTitle="Cost (USD)"
          i18nStrings={{
            filterLabel: 'Filter services',
            filterPlaceholder: 'Filter services',
            filterSelectedAriaLabel: 'selected',
            detailPopoverDismissAriaLabel: 'Dismiss',
            legendAriaLabel: 'Legend',
            chartAriaRoleDescription: 'area chart',
            xAxisAriaRoleDescription: 'X axis',
            yAxisAriaRoleDescription: 'Y axis',
          }}
        />
      )}
    </Container>
  );
};

// Component for Cost Overview section with Pie Chart
const CostOverview: React.FC<{
  timePeriod?: { value?: string };
  environment?: string;
  pipelines?: Pipeline[];
  pipelinesLoading?: boolean;
  onRefresh?: () => void;
  onTotalCostUpdate?: (cost: number) => void;
}> = ({
  timePeriod,
  environment,
  pipelines = [],
  pipelinesLoading = false,
  onRefresh,
  onTotalCostUpdate,
}) => {
  const [costData, setCostData] = useState<ServiceCostBreakdown[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCost, setTotalCost] = useState<number>(0);

  // Map service names to colors for consistency
  const getServiceColor = (service: string): string => {
    const colorMap: { [key: string]: string } = {
      S3: '#1f77b4',
      Lambda: '#ff7f0e',
      DynamoDB: '#2ca02c',
      'Kinesis Firehose': '#d62728',
      Kinesis: '#9467bd',
      SQS: '#8c564b',
      SNS: '#e377c2',
      Other: '#7f7f7f',
    };
    return colorMap[service] || '#bcbd22';
  };

  const processCostData = async () => {
    try {
      setLoading(true);
      setError(null);

      const timeframeValue = (timePeriod?.value as TimeframePeriod) || 'weekly';

      // Check if pipelines have cost data loaded
      const pipelinesWithCostData = pipelines.filter(
        (p: any) => p.pipeline_cost_summary && Object.keys(p.pipeline_cost_summary).length > 0,
      );

      if (pipelinesWithCostData.length === 0) {
        // If no pipelines have cost data, we need to load it
        const authSession = await fetchAuthSession();
        let totalCalculatedCost = 0;
        const serviceBreakdown = new Map<string, number>();

        for (const pipeline of pipelines) {
          if (pipeline.status === 'disabled') {
            continue;
          }

          try {
            // Use the same identifier logic as the pipelines table
            const pipelineIdentifier = (pipeline as any).filename || pipeline.name;

            const pipelineCost = await apiService.getPipelineCost(
              pipelineIdentifier,
              timeframeValue,
              authSession,
              2025,
              false, // Use cache
            );

            if (pipelineCost && !pipelineCost.error && pipelineCost.weekly_cost > 0) {
              totalCalculatedCost += pipelineCost.weekly_cost;

              // Now get detailed pipeline data to break down by service
              try {
                const detailedResponse = await apiService.getPipeline(
                  pipelineIdentifier,
                  authSession,
                  timeframeValue,
                  false, // Use cache
                );

                // Process nodes to get service breakdown
                if (detailedResponse.pipeline.nodes) {
                  for (const node of detailedResponse.pipeline.nodes) {
                    const costMetrics = (node as any).cost_metrics?.['2025'];
                    if (costMetrics && !costMetrics.error && costMetrics.weekly_cost > 0) {
                      // Map component type to service category using the same logic as the API service
                      let serviceCategory = 'Other';
                      if ((node as any).type) {
                        const componentType = (node as any).type;
                        if (
                          componentType.startsWith('processor.lambda') ||
                          componentType.startsWith('processor.')
                        ) {
                          serviceCategory = 'Lambda';
                        } else if (componentType === 'buffer.sqs') {
                          serviceCategory = 'SQS';
                        } else if (componentType === 'buffer.kinesis') {
                          serviceCategory = 'Kinesis';
                        } else if (componentType === 'buffer.s3_firehose') {
                          serviceCategory = 'Kinesis Firehose';
                        } else if (componentType === 'storage.s3') {
                          serviceCategory = 'S3';
                        } else if (componentType === 'storage.dynamodb') {
                          serviceCategory = 'DynamoDB';
                        } else if (componentType === 'source.sns') {
                          serviceCategory = 'SNS';
                        }

                        const currentCost = serviceBreakdown.get(serviceCategory) || 0;
                        serviceBreakdown.set(
                          serviceCategory,
                          currentCost + costMetrics.weekly_cost,
                        );
                      }
                    }
                  }
                } else {
                  // Fallback: assign all cost to Lambda if no detailed breakdown available
                  const currentCost = serviceBreakdown.get('Lambda') || 0;
                  serviceBreakdown.set('Lambda', currentCost + pipelineCost.weekly_cost);
                }
              } catch (detailError) {
                // Fallback: assign all cost to Lambda if detailed call fails
                const currentCost = serviceBreakdown.get('Lambda') || 0;
                serviceBreakdown.set('Lambda', currentCost + pipelineCost.weekly_cost);
              }
            }
          } catch (error) {
            // Silently continue on error
          }
        }

        // Convert to result format
        const result = Array.from(serviceBreakdown.entries()).map(([service, cost]) => ({
          service,
          cost,
          percentage: 0,
        }));

        const total = result.reduce((sum, item) => sum + item.cost, 0);
        result.forEach((item) => {
          item.percentage = total > 0 ? (item.cost / total) * 100 : 0;
        });

        setCostData(result);
        setTotalCost(totalCalculatedCost);

        // Notify parent component of the total cost
        if (onTotalCostUpdate) {
          onTotalCostUpdate(totalCalculatedCost);
        }
      } else {
        // Use the already loaded pipelines data to calculate service breakdown
        const result = await apiService.getAggregatedCostsFromPipelines(
          pipelines as any[], // Cast to ApiPipeline[] since they should have the same structure
          timeframeValue,
          2025, // Default to 2025 costs
        );

        setCostData(result.serviceBreakdown);
        setTotalCost(result.totalCost);

        // Notify parent component of the total cost
        if (onTotalCostUpdate) {
          onTotalCostUpdate(result.totalCost);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process cost data');
      setCostData([]);
      setTotalCost(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only process cost data when pipelines are loaded and not loading
    if (!pipelinesLoading && pipelines.length > 0) {
      processCostData();
    } else if (!pipelinesLoading) {
      // If no pipelines, set empty state
      setCostData([]);
      setTotalCost(0);
      setLoading(false);
    }
  }, [timePeriod?.value, environment, pipelines, pipelinesLoading]);

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh(); // This will trigger the parent to refresh pipelines data
    }
  };

  // Transform data for PieChart component
  const pieChartData = costData.map((item) => ({
    title: item.service,
    value: item.cost,
    color: getServiceColor(item.service),
  }));

  return (
    <Container
      header={
        <Header
          actions={
            <Button iconName="refresh" variant="normal" onClick={handleRefresh} loading={loading}>
              Refresh
            </Button>
          }
          description={`Cost breakdown by AWS service category (${
            timePeriod?.value || 'weekly'
          } costs)`}
          variant="h2"
        >
          Cost overview
        </Header>
      }
      data-venue="true"
    >
      <SpaceBetween size="m">
        {/* Total cost summary */}
        <KeyValuePairs
          columns={3}
          items={[
            {
              label: 'Total cost',
              value: loading ? 'Loading...' : formatCurrency(totalCost),
            },
            {
              label: 'Time period',
              value:
                (timePeriod?.value || 'weekly').charAt(0).toUpperCase() +
                (timePeriod?.value || 'weekly').slice(1),
            },
            {
              label: 'Services',
              value: loading ? 'Loading...' : costData.length.toString(),
            },
          ]}
        />

        {/* Pie chart */}
        {loading ? (
          <Box color="inherit" textAlign="center" padding="l">
            <Box fontWeight="bold">Loading cost data...</Box>
          </Box>
        ) : error ? (
          <Box color="inherit" textAlign="center" padding="l">
            <Box fontWeight="bold" color="text-status-error">
              Error loading cost data
            </Box>
            <Box color="inherit" variant="p" margin={{ top: 's' }}>
              {error}
            </Box>
          </Box>
        ) : pieChartData.length === 0 ? (
          <Box color="inherit" textAlign="center" padding="l">
            <Box fontWeight="bold">No cost data available</Box>
            <Box color="inherit" variant="p">
              There is no cost data to display for the selected time period
            </Box>
          </Box>
        ) : (
          <PieChart
            data={pieChartData}
            detailPopoverContent={(datum) => [
              { key: 'Service', value: datum.title },
              { key: 'Cost', value: formatCurrency(datum.value) },
              {
                key: 'Percentage',
                value: `${((datum.value / totalCost) * 100).toFixed(1)}%`,
              },
            ]}
            hideFilter
            hideLegend={false}
            i18nStrings={{
              filterLabel: 'Filter services',
              filterPlaceholder: 'Filter services',
              filterSelectedAriaLabel: 'selected',
              detailPopoverDismissAriaLabel: 'Dismiss',
              legendAriaLabel: 'Legend',
              chartAriaRoleDescription: 'pie chart',
              segmentAriaRoleDescription: 'segment',
            }}
            size="medium"
            variant="pie"
          />
        )}
      </SpaceBetween>
    </Container>
  );
};

// Main Dashboard component
const DashboardPage: React.FC<DashboardProps> = ({
  environment,
  onEnvironmentChange,
  timePeriod,
  onTimePeriodChange,
  darkMode,
  onDarkModeChange,
  givenName,
  pipelines,
  pipelinesLoading,
  onRefresh,
}) => {
  // Shared state for total cost between components
  const [totalCost, setTotalCost] = useState<number>(0);

  // Callback function to update total cost from CostOverview
  const handleTotalCostUpdate = (cost: number) => {
    setTotalCost(cost);
  };
  return (
    <SpaceBetween size="l">
      <DashboardHeader
        environment={environment}
        onEnvironmentChange={onEnvironmentChange}
        timePeriod={timePeriod}
        onTimePeriodChange={onTimePeriodChange}
        darkMode={darkMode}
        onDarkModeChange={onDarkModeChange}
      />

      <Grid
        gridDefinition={[
          {
            colspan: {
              default: 12,
              l: 12,
              m: 12,
            },
          },
          {
            colspan: {
              default: 12,
              l: 8,
              m: 8,
            },
          },
          {
            colspan: {
              default: 12,
              l: 4,
              m: 4,
            },
          },
          {
            colspan: {
              default: 12,
              l: 6,
              m: 6,
            },
          },
          {
            colspan: {
              default: 12,
              l: 6,
              m: 6,
            },
          },
        ]}
      >
        <WelcomeMessage givenName={givenName} />
        <ServiceOverview
          pipelines={pipelines}
          pipelinesLoading={pipelinesLoading}
          totalCost={totalCost}
          timePeriod={timePeriod}
          environment={environment}
        />
        <ServiceHealth pipelines={pipelines} />
        <CostTrends
          onRefresh={onRefresh}
          pipelines={pipelines}
          pipelinesLoading={pipelinesLoading}
          timePeriod={timePeriod}
          environment={environment}
        />
        <CostOverview
          timePeriod={timePeriod}
          environment={environment}
          pipelines={pipelines}
          pipelinesLoading={pipelinesLoading}
          onRefresh={onRefresh}
          onTotalCostUpdate={handleTotalCostUpdate}
        />
      </Grid>
    </SpaceBetween>
  );
};

export default DashboardPage;
